import { h, Component, render } from 'preact';
import { joinClasses } from 'utils/utils';
import { mod } from 'utils/math';

import Button from 'components/button/button';

import s from 'components/inputWithKeywords/inputWithKeywords.sss';

export default class InputWithKeywordsReplaced extends Component {
	constructor(props) {
		super(props);

		this.state = {
			activeOptions: [],
			selectedOption: 0,
		};

		this.ranges = [];

		this.onBlur = this.onBlur.bind(this);
		this.onInput = this.onInput.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.onOptionClick = this.onOptionClick.bind(this);
		this.onSelectionChange = this.onSelectionChange.bind(this);
		this.onBeforeInput = this.onBeforeInput.bind(this);
		this.checkSuggestions = this.checkSuggestions.bind(this);
		this.onHighlightClick = this.onHighlightClick.bind(this);
		this.onCopy = this.onCopy.bind(this);
		this.onCut = this.onCut.bind(this);
	}

	get value() {
		const input = this.getInput();
		if (!input) return this.props.value || '';
		return [...input.childNodes].map(node => node.dataset?.value || node.textContent).join('');
	}

	set value(value) {
		const input = this.getInput();
		if (!input) return;

		const keywords = this.props.keywords || [];
		const regexContent = '%(' + keywords.map(keyword => RegExp.escape(keyword.value)).join('|') + ')%';
		if (value === this.value) {
			// Only update things if there is an unwrapped keyword
			const unwrappedCheck = new RegExp('(?<!<span[^>]*>|data-value=")' + regexContent, 'g');
			if (!unwrappedCheck.test(input.innerHTML)) {
				return;
			}
		}

		const [selectionStart, selectionEnd] = document.activeElement === input ? this.getContentEditableSelection(input) : [];

		const regex = new RegExp(regexContent, 'g');
		const fragment = document.createDocumentFragment();
		let previousIndex = 0;
		let match;
		while (keywords.length && (match = regex.exec(value))) {
			fragment.append(value.slice(previousIndex, match.index));
			const highlight = document.createElement('span');
			highlight.classList.add(s.keyword);
			highlight.contentEditable = 'false';
			highlight.dataset.value = match[0];
			const keyword = keywords.find(kw => kw.value === match[1]);
			if (keyword?.label && (!Array.isArray(keyword.label) || keyword.label.length)) {
				const tmp = document.createElement('div');
				render(<div>{keyword.label}</div>, tmp);
				highlight.append(...tmp.firstElementChild.childNodes);
			} else {
				highlight.textContent = match[1];
			}
			highlight.addEventListener('click', this.onHighlightClick);
			fragment.append(highlight);
			previousIndex = match.index + match[0].length;
		}
		fragment.append(value.slice(previousIndex));
		input.replaceChildren(fragment);

		if (selectionStart !== undefined) {
			this.setSelection(selectionStart, selectionEnd);
		}

		this.base.querySelector('input').value = value;
		this.checkSuggestions();
	}

	componentDidMount() {
		// The native input can listen to the event on the element itself, but the contenteditable fallback needs to listen globally
		document.addEventListener('selectionchange', this.onSelectionChange);
		if (this.props.value) {
			this.value = this.props.value;
		}
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.value !== this.props.value) {
			this.value = nextProps.value;
		}
	}

	componentWillUnmount() {
		document.removeEventListener('selectionchange', this.onSelectionChange);
	}

	getInput() {
		return this.base?.querySelector(`.${s.content}`);
	}

	onBlur(e) {
		if (this.base.contains(e.relatedTarget)) return;

		// Still have to check for the actual focus to change, sometimes relatedTarget is null
		setTimeout(() => {
			if (this.base && !this.base.contains(document.activeElement)) {
				this.hideDropdown();
			}
		}, 0);
	}

	onInput() {
		this.props.onInput?.({ target: this, currentTarget: this });

		// Re-assign value to ensure the keywords are wrapped
		// eslint-disable-next-line no-self-assign
		this.value = this.value;
	}

	onBeforeInput(e) {
		// Avoid any newline
		if (this.props.readonly || e.inputType === 'insertLineBreak' || e.inputType === 'insertParagraph') {
			e.preventDefault();
		}
		if (e.data?.includes('\n')) {
			const range = document.getSelection()?.getRangeAt(0);
			if (range) {
				e.preventDefault();
				const toInsert = e.data.replace(/^\n+|\n+$/g, '').replace(/\n+/g, ' ');
				range.deleteContents();
				range.insertNode(document.createTextNode(toInsert));
				range.collapse();
				this.getInput()?.dispatchEvent(new Event('input'));
			}
		}
		// When only one single keyword is in the input, it cannot be removed via backspace for some reason
		if (e.inputType === 'deleteContentBackward') {
			const input = this.getInput();
			const nonEmptyChildren = input && [...input.childNodes].filter(n => n.textContent !== '');
			if (nonEmptyChildren?.length === 1 && nonEmptyChildren[0].classList?.contains(s.keyword)) {
				e.preventDefault();
				nonEmptyChildren[0].remove();
				input.dispatchEvent(new Event('input'));
			}
		}
		this.props.onBeforeInput?.(e);
	}

	onKeyDown(e) {
		const state = this.state;
		if (this.isDropdownShown()) {
			switch (e.key) {
				case 'Enter':
				case 'Tab':
					e.preventDefault();
					this.useSuggestion(state.activeOptions?.[state.selectedOption]);
					return;
				case 'Escape':
					e.preventDefault();
					this.hideDropdown();
					return;
				case 'ArrowDown':
				case 'ArrowUp':
					e.preventDefault();
					const offset = e.key === 'ArrowDown' ? 1 : -1;
					this.setState({ selectedOption: this.getSelectableOption(offset) });
					return;
			}
		}
		this.props.onKeyDown?.(e);
	}

	onOptionClick(e) {
		const button = e.currentTarget;
		const value = button?.value;
		this.useSuggestion(this.state.activeOptions.find(opt => opt.value === value));
	}

	getSelectableOption(offset) {
		const { activeOptions, selectedOption } = this.state;
		for (let i = 1; i <= activeOptions.length; i++) {
			const index = mod(selectedOption + offset * i, activeOptions.length);
			if (!activeOptions[index]?.disabled) return index;
		}
		return selectedOption;
	}

	onSelectionChange() {
		if (document.activeElement === this.getInput()) {
			this.checkSuggestions();
		}
	}

	onHighlightClick(e) {
		const highlight = e.currentTarget;
		// Set cursor after the clicked element
		const range = document.createRange();
		range.setStartBefore(highlight);
		// range.setStartAfter(highlight);
		range.setEndAfter(highlight);
		const selection = document.getSelection();
		selection?.removeAllRanges();
		selection?.addRange(range);
	}

	useSuggestion(suggestion) {
		if (!suggestion || suggestion.disabled) return;
		const [from, to] = this.state.activeRange || [];
		const input = this.getInput();
		if (from == null || to == null || !input) return;
		const setSelectionTo = from + suggestion.value.length + 2;

		const value = this.value;
		this.value = `${value.slice(0, from)}%${suggestion.value}%${value.slice(to)}`;

		this.setSelection(setSelectionTo);

		this.clearSuggestions();
		input.focus();
		input.dispatchEvent(new Event('input'));
	}

	getContext(value, at) {
		const slashContext = value.slice(0, at).match(/(?<=^|\s)\/([\w-]*)$/);
		if (!slashContext) return null;
		const contextAfter = value.slice(at).match(/^([\w-]*)/);

		return {
			from: slashContext.index,
			to: at + (contextAfter?.[0].length || 0),
			textBeforeCursor: slashContext[1],
			textAfterCursor: contextAfter?.[1],
		};
	}

	checkSuggestions() {
		const state = this.state;
		const input = this.getInput();
		if (!input?.matches(':focus-within')) return;

		const value = this.value;
		const at = this.getContentEditableSelection()[0];
		if (at == undefined) return this.clearSuggestions();

		const context = this.getContext(value, at);
		if (!context) return this.clearSuggestions();
		const { from, to, textBeforeCursor, textAfterCursor } = context;

		const search = new RegExp(RegExp.escape(textBeforeCursor) + '.*' + RegExp.escape(textAfterCursor || ''), 'i');
		const options = this.props.keywords?.filter(keyword => search.test(keyword.value)) || [];
		if (from === state.activeRange?.[0] && to === state.activeRange?.[1] && options.length === state.activeOptions.length && options.every((opt, i) => opt === state.activeOptions[i])) {
			// Suggestions have not changed, no need to update
			// But they might be hidden (if the input was blurred)
			if (options.length) this.showDropdown();
			return;
		}

		this.setState({ activeOptions: options, selectedOption: options.findIndex(option => !option.disabled), activeRange: [from, to] });
		if (options.length) {
			const position = this.getCaretPosition(input, from);
			this.showDropdown(position);
		} else {
			this.clearSuggestions();
		}
	}

	getCaretPosition(input, char) {
		const range = document.createRange();
		range.setStart(input, 0);
		range.setEnd(...this.getRangePositionFromOffset(input, char));
		return range.getBoundingClientRect().width;
	}

	showDropdown(position) {
		const dropdown = this.base.querySelector(`.${s.dropdown}`);
		if (position != undefined) dropdown.style.left = `${position}px`;
		dropdown?.togglePopover(true);
	}

	hideDropdown() {
		this.base.querySelector(`.${s.dropdown}`)?.togglePopover(false);
	}

	isDropdownShown() {
		return this.base.querySelector(`.${s.dropdown}`)?.matches(':popover-open') || false;
	}

	clearSuggestions() {
		if (this.state.activeOptions.length > 0) {
			this.setState({ activeOptions: [], selectedOption: 0 });
		}
		this.hideDropdown();
	}

	getContentEditableSelection(input = this.getInput()) {
		const selection = document.getSelection();

		if (!selection?.rangeCount || !input.contains(selection.anchorNode)) {
			return [];
		}

		const range = selection.getRangeAt(0);
		return [
			this.getOffsetFromRangePosition(range.startContainer, range.startOffset),
			this.getOffsetFromRangePosition(range.endContainer, range.endOffset),
		];
	}

	getOffsetFromRangePosition(rangeNode, rangePosition) {
		const input = this.getInput();
		const nodes = input.childNodes;
		let offset = 0;
		for (const node of nodes) {
			if (node instanceof Element && node.classList.contains(s.keyword)) {
				const length = node.dataset?.value?.length || node.textContent.length;
				if (node === rangeNode) return offset;
				offset += length;
			} else if (node.nodeType === Node.TEXT_NODE) {
				if (node === rangeNode) return offset + rangePosition;
				offset += node.length;
			}
		}
		return offset;
	}

	getRangePositionFromOffset(input = this.getInput(), offset) {
		const nodes = input.childNodes;
		let traversed = 0;
		for (const node of nodes) {
			if (node instanceof Element && node.classList.contains(s.keyword)) {
				const length = node.dataset?.value?.length || node.textContent.length;
				if (traversed + length > offset) {
					if (!node.nextSibling) break;
					return [node.nextSibling, 0];
				}
				traversed += length;
			} else if (node.nodeType === Node.TEXT_NODE) {
				if (traversed + node.length > offset) {
					return [node, offset - traversed];
				}
				traversed += node.length;
			}
		}
		return [input, input.childNodes.length];
	}

	setSelection(selectionStart, selectionEnd = selectionStart) {
		const input = this.getInput();
		const range = document.createRange();
		const fromRangePosition = this.getRangePositionFromOffset(input, selectionStart);
		const toRangePosition = selectionEnd === selectionStart ? fromRangePosition : this.getRangePositionFromOffset(input, selectionEnd);
		range.setStart(...fromRangePosition);
		range.setEnd(...toRangePosition);
		const selection = document.getSelection();
		selection.removeAllRanges();
		selection.addRange(range);
	}

	onCopy(e) {
		e.preventDefault();
		const selection = this.getContentEditableSelection();
		e.clipboardData.setData('text/plain', this.value.slice(...selection));
	}
	onCut(e) {
		e.preventDefault();
		const selection = this.getContentEditableSelection();
		e.clipboardData.setData('text/plain', this.value.slice(...selection));
		const documentSelection = document.getSelection();
		documentSelection.deleteFromDocument();
	}

	render(props, state) {
		const { class: className, keywords, value, ...passthrough } = props;
		return (
			<div class={joinClasses(s.container, className)} onFocusOut={this.onBlur}>
				<input {...passthrough} type="hidden" onInput={this.onInput} onKeyDown={this.onKeyDown} onSelectionChange={this.checkSuggestions} />
				<Uncontrolled class={s.content} contenteditable="plaintext-only" onBeforeInput={this.onBeforeInput} onInputCapture={this.onInput} onKeyDown={this.onKeyDown} onFocusIn={this.checkSuggestions} onCopy={this.onCopy} onCut={this.onCut} />
				<div class={s.dropdown} popover="manual">
					{state.activeOptions.map((option, i) => (
						<Button type="button" class={joinClasses(i === state.selectedOption && s.active)} value={option.value} disabled={option.disabled} onClick={this.onOptionClick} tabIndex="-1">
							<div class={s.label}>{option.label}</div>
							<div class={s.value}>{option.example || option.value}</div>
						</Button>
					))}
				</div>
			</div>
		);
	}
}

class Uncontrolled extends Component {
	shouldComponentUpdate() {
		return false;
	}

	render(props) {
		return <div {...props} />;
	}
}